import React, { useState } from 'react';
import Form from '@rjsf/core';
import { BaseMetadataDocument, copyDocument } from '@sgdocs/client';
import {
  computeSchema,
  computeUiSchema,
  flattenFormData,
  JsonSchemaWidgets,
} from '../common/json-schema/JsonSchemaForm';
import { validate } from 'jsonschema';
import { useQueryGetModel } from '../common/hooks/useQueryGetModel';
import { Spinner } from 'reactstrap';
import { DsButtonToggle, DsContextualHelp, DsSgDatePicker } from '@sgdocs/design-system';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '../common/hooks/queryClient';
import { openHelpCenter } from "../common/widget/help-center";
import { v4 as uuidv4 } from 'uuid';
import { EWidgetsSgmDocs } from '../common/widget/enum';

const defaultLabels = {
  commonCancel: 'Cancel',
  commonCopy: 'Paste',
  commonNo: 'No',
  commonYes: 'Yes',
  documentFilename: 'Filename(s)',
  documentMetadata: 'Metadata',
  documentModelName: 'Model',
  documentProperties: 'Properties',
  documentPropertiesConfidentialityLevel: 'Confidentiality',
  documentPropertiesContentOutsideSgNetwork: 'Outside SG network',
  documentPropertiesDestroyDate: 'Destroy date',
  documentWorkspaceName: 'Workspace',
  targetWorkspaceName: 'Target workspace',
  targetModelName: 'Target model',
  modelFetchError: 'An error occurred while trying to retrieve model info',
  useTargetMetadata: 'Use target model metadata'
};

interface ICopyDocumentFormComponentProps {
  filenames: string[];
  workspaceId: string;
  modelId: string;
  documentId: string;
  versionId: string;
  toWorkspaceId: string;
  toModelId: string;
  documentFormData: any;
  confidentialityLevel: string;
  destroyDate?: string;
  contentsOutsideSgNetwork: boolean;
  workspaceName: string;
  targetWorkspaceName: string;
  onCopyCompleted: (documentVersionIdentifier: { workspaceId: string, modelId: string, documentId: string, versionId: string }) => void;
  onCancel: () => void;
  sgmDocsBaseUrl: string;
  labels?: any;
  locale?: string;
  token?: string | null;
}

const CopyDocumentFormComponent = ({
  filenames,
  workspaceId,
  modelId,
  documentId,
  versionId,
  toWorkspaceId,
  toModelId,
  documentFormData,
  confidentialityLevel,
  destroyDate = '',
  contentsOutsideSgNetwork,
  workspaceName,
  targetWorkspaceName,
  onCopyCompleted,
  onCancel,
  sgmDocsBaseUrl,
  labels,
  locale = 'en',
  token = null,
}: ICopyDocumentFormComponentProps): JSX.Element => {
  const [useTargetMetadata, setUseTargetMetadata] = useState<boolean>();
  const [formData, setFormData] = useState(documentFormData);
  const [documentConfidentialityLevel, setDocumentConfidentialityLevel] = useState<string>(confidentialityLevel);
  const [documentDestroyDate, setDocumentDestroyDate] = useState<string>(destroyDate);
  const [documentContentsOutsideSGNetwork, setDocumentContentsOutsideSGNetwork] = useState<boolean>(contentsOutsideSgNetwork);
  const [extraErrors, setExtraErrors] = useState<any>({});
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const { data: model, isError } = useQueryGetModel({ workspaceId, modelId }, { token, sgmDocsBaseUrl, component: EWidgetsSgmDocs.COPY_DOCUMENT_FORM });
  const { data: targetModel } = useQueryGetModel({ workspaceId: toWorkspaceId, modelId: toModelId }, { token, sgmDocsBaseUrl, component: EWidgetsSgmDocs.COPY_DOCUMENT_FORM });

  const labelsIntl = {
    ...defaultLabels,
    ...labels,
  };

  if (isError || !model || !targetModel) {
    return <div className="m-2">{labelsIntl.modelFetchError}</div>;
  } else if (useTargetMetadata === undefined) {
    const shouldUseTargetMetadata: boolean = JSON.stringify(model.jsonSchema) !== JSON.stringify(targetModel.jsonSchema);
    setFormData(shouldUseTargetMetadata ? {} : documentFormData);
    setUseTargetMetadata(shouldUseTargetMetadata);
  }

  const schema = useTargetMetadata ? computeSchema(targetModel.jsonSchema) : computeSchema(model.jsonSchema);
  const uiSchema = computeUiSchema(schema, 2);

  let destroyDatePlaceholder: string | undefined = undefined;
  const offsetMatches = targetModel?.defaultExpiry?.match(/P([0-9]+Y)?([0-9]+M)?([0-9]+D)?/);
  if (offsetMatches) {
    const defaultDestroyDate = new Date();
    defaultDestroyDate.setFullYear(
      defaultDestroyDate.getFullYear() +
      (offsetMatches[1] !== undefined ? parseInt(offsetMatches[1].replace('Y', '')) : 0)
    );
    defaultDestroyDate.setMonth(
      defaultDestroyDate.getMonth() + (offsetMatches[2] !== undefined ? parseInt(offsetMatches[2].replace('M', '')) : 0)
    );
    defaultDestroyDate.setDate(
      defaultDestroyDate.getDate() + (offsetMatches[3] !== undefined ? parseInt(offsetMatches[3].replace('D', '')) : 0)
    );
    const year = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(defaultDestroyDate);
    const month = new Intl.DateTimeFormat(locale, { month: '2-digit' }).format(defaultDestroyDate);
    const day = new Intl.DateTimeFormat(locale, { day: '2-digit' }).format(defaultDestroyDate);

    destroyDatePlaceholder = `${day}/${month}/${year}`;
  }

  const handleCopy = async (newDocument: any, jsonSchema: any): Promise<boolean> => {
    const formattedFormData = flattenFormData(newDocument.schema, newDocument.formData);
    const result = validate(formattedFormData, jsonSchema, { base: 'http://localhost' });
    const errors = result.errors;
    if (errors && errors.length > 0) {
      const newErrors: any = {};
      errors
        .forEach((error: any) => {
          const message = error.name === 'required' ? error.message : `Please check ${error.argument} definition in JSON schema`;
          console.error(error);
          newErrors[error.argument] = {
            __errors: [message],
          };
        });
      setExtraErrors(newErrors);
      setIsCopying(false);
      return false;
    } else {
      const tokenUsed: string = token
        ? token
        : window.SGWTWidgetConfiguration.bus.dangerouslyGetCurrentValue('sg-connect.access-token') || '';

      setExtraErrors({});
      try {
        const copiedDocument: BaseMetadataDocument =
          await copyDocument({
            workspaceId: workspaceId,
            documentId: documentId,
            versionId: versionId,
            toWorkspaceId: toWorkspaceId,
            toModelId: toModelId,
            useTargetMetadata: useTargetMetadata!,
            metadataToAdd: formattedFormData,
            confidentialityLevel: documentConfidentialityLevel,
            destroyDate: documentDestroyDate,
            contentsOutsideSGNetwork: !targetModel.allowContentsOutsideSGNetwork ? false : documentContentsOutsideSGNetwork,
            authorizationHeader: tokenUsed,
            baseUrl: sgmDocsBaseUrl,
            component: EWidgetsSgmDocs.COPY_DOCUMENT_FORM
          }).finally(() => setIsCopying(false));
        onCopyCompleted(copiedDocument.documentVersionIdentifier);
        return true;
      } catch (error) {
        console.error('Error copying', error);

        const newErrors: any = {};
        newErrors["Error during copy error"] = {
          __errors: [error],
        };
        setExtraErrors(newErrors);
        setIsCopying(false)
        return false;
      }
    }
  };

  const submitForm = (): void => {
    setIsCopying(true);
    document.getElementById('copy-document-button')?.click();
  };

  function getConfidentialityLevelDefaultValue(): string {
    if (!targetModel) {
      return documentConfidentialityLevel;
    }
    try {
      if (Number(documentConfidentialityLevel.substring(1, 2)) < Number(targetModel.defaultConfidentialityLevel.substring(1, 2))) {
        return targetModel.defaultConfidentialityLevel;
      } else {
        return documentConfidentialityLevel;
      }
    } catch (e) {
      console.error(e)
      return targetModel.defaultConfidentialityLevel;
    }
  }

  return (
    <>
      <div className={'modal-body'}>
        <div className={'form-group mb-0'}>
          <label>{labelsIntl.documentFilename}</label>
        </div>
        {filenames &&
          filenames.length > 0 &&
          filenames.map((filename) => (
            <>
              <label className={'mb-1'} key={filename}>
                {filename}
              </label>
              <br />
            </>
          ))}
        <div className={'form-group field field-string mt-4'}>
          <label>{labelsIntl.documentWorkspaceName}</label>
          {workspaceName == targetWorkspaceName ? <p>{workspaceName}</p> : <p>{workspaceName} <i className={`icon icon-md ms-1 me-1`}>{'east'}</i> {targetWorkspaceName}</p>}
        </div>
        <div className={'form-group field field-string'}>
          <label>{labelsIntl.documentModelName}</label>
          {model.name == targetModel.name ? <p>{model.name}</p> : <p>{model.name} <i className={`icon icon-md ms-1 me-1`}>{'east'}</i> {targetModel.name}</p>}
        </div>
        <div className={'mb-4'}>
          <h4>{labelsIntl.documentProperties}</h4>
          <div className={'px-0'}>
            <div className={'form-group field field-object'}>
              <fieldset id={'add-properties'}>
                <div className={'form-group field field-string col-md-6'}>
                  <DsButtonToggle
                    id={'confidentiality-level'}
                    label={<>{labelsIntl.documentPropertiesConfidentialityLevel}</>}
                    items={[
                      {
                        label: 'C0',
                        value: 'C0',
                        disable: targetModel.useAip || targetModel.defaultConfidentialityLevel === 'C1' || targetModel.defaultConfidentialityLevel === 'C2' || targetModel.defaultConfidentialityLevel === 'C3',
                      },
                      {
                        label: 'C1',
                        value: 'C1',
                        disable: targetModel.useAip || targetModel.defaultConfidentialityLevel === 'C2' || targetModel.defaultConfidentialityLevel === 'C3',
                      },
                      {
                        label: 'C2',
                        value: 'C2',
                        disable: targetModel.useAip || targetModel.defaultConfidentialityLevel === 'C3',
                      },
                      { label: 'C3', value: 'C3', disable: targetModel.useAip }
                    ]}
                    defaultValue={getConfidentialityLevelDefaultValue()}
                    onChange={setDocumentConfidentialityLevel}
                  />
                </div>
                <div className={'form-group field field-string col-md-6'}>
                  <DsButtonToggle
                    id={'content-outside-sg-network'}
                    label={<>{labelsIntl.documentPropertiesContentOutsideSgNetwork}</>}
                    items={[
                      {
                        label: labelsIntl.commonYes,
                        value: 'true',
                        disable: !targetModel.allowContentsOutsideSGNetwork || targetModel.useAip,
                      },
                      {
                        label: labelsIntl.commonNo,
                        value: 'false',
                        disable: !targetModel.allowContentsOutsideSGNetwork || targetModel.useAip,
                      },
                    ]}
                    defaultValue={!targetModel.allowContentsOutsideSGNetwork ? 'false' : documentContentsOutsideSGNetwork ? 'true' : 'false'}
                    onChange={(value: string): void => setDocumentContentsOutsideSGNetwork(value === 'true')}
                  />
                </div>
                <div className={'form-group field field-string col-md-6'}>
                  <label>{labelsIntl.documentPropertiesDestroyDate}</label>
                  <DsContextualHelp id={'top.docs_def_destroy'} openContextualHelp={openHelpCenter} />
                  <DsSgDatePicker
                    id={'destroy-date'}
                    onChange={(date: string | undefined): void => setDocumentDestroyDate(date ? date.split('T')[0] : '')}
                    placeholder={destroyDatePlaceholder}
                    displayFormat={'dd/MM/yyyy'}
                    locale={locale}
                    initDate={new Date(documentDestroyDate)}
                  />
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <h4>{labelsIntl.documentMetadata}</h4>
        <div className={'px-0 pt-2'}>
          <Form
            idPrefix={'copy-document'}
            name={'copy-document-form'}
            schema={schema}
            uiSchema={uiSchema}
            widgets={JsonSchemaWidgets}
            formData={formData}
            extraErrors={extraErrors}
            onChange={(e): void => setFormData(e.formData)}
            noHtml5Validate
            noValidate
            onSubmit={(newDocument) => handleCopy(newDocument, useTargetMetadata ? targetModel.jsonSchema : model.jsonSchema)}>
            <button id="copy-document-button" type="submit" style={{ display: 'none' }} />
          </Form>
        </div>
      </div>
      <div className={'modal-footer'}>
        <div className={'d-flex flex-row-reverse'}>
          <button className={'btn btn-lg btn-primary mt-2'} onClick={submitForm}>
            {isCopying ? <Spinner style={{ width: '1rem', height: '1rem' }} /> : labelsIntl.commonCopy}
          </button>
          <button className={'btn btn-lg btn-default mt-2 me-4'} onClick={onCancel}>
            {labelsIntl.commonCancel}
          </button>
        </div>
      </div>
    </>
  );
};

export const SGMDocsCopyDocumentForm = ({
  filenames,
  workspaceId,
  modelId,
  documentId,
  versionId,
  toWorkspaceId,
  toModelId,
  documentFormData,
  confidentialityLevel,
  destroyDate = '',
  contentsOutsideSgNetwork,
  workspaceName,
  targetWorkspaceName,
  onCopyCompleted,
  onCancel,
  sgmDocsBaseUrl,
  labels,
  locale,
  token
}: ICopyDocumentFormComponentProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <CopyDocumentFormComponent
        key={uuidv4()}
        filenames={filenames}
        workspaceId={workspaceId}
        modelId={modelId}
        documentId={documentId}
        versionId={versionId}
        toWorkspaceId={toWorkspaceId}
        toModelId={toModelId}
        documentFormData={documentFormData}
        confidentialityLevel={confidentialityLevel}
        destroyDate={destroyDate}
        contentsOutsideSgNetwork={contentsOutsideSgNetwork}
        workspaceName={workspaceName}
        targetWorkspaceName={targetWorkspaceName}
        onCopyCompleted={onCopyCompleted}
        onCancel={onCancel}
        sgmDocsBaseUrl={sgmDocsBaseUrl}
        labels={labels}
        locale={locale}
        token={token}
      />
    </QueryClientProvider>
  );
}
